// ANGEL FLIGHT — the Gunner (player). First-person: no body sprite, just the
// aim point, hull, and the muzzle origin the gun fires from. Satisfies GunnerRef.

import { BALANCE } from '../config/balance';
import type { GunnerRef } from '../systems/System';
import type { Vec2, WeaponId } from '../core/types';

export class Gunner implements GunnerRef {
  aim: Vec2;
  firing = false;
  spinup = 0;
  activeWeapon: WeaponId = 'minigun';
  hull: number;
  readonly maxHull: number;
  flareActiveMs = 0;
  flareCdMs = 0;
  private invulnMs = 0;
  private recoil = 0;
  /** Field Repair (autoRepair upgrade): hull HP regenerated per second once the
   *  bird has been out of contact for autoRepairDelayMs. 0 = un-upgraded (no
   *  regen — the default, so an unbought bird behaves exactly as before). */
  private readonly repairPerSec: number;
  /** ms since the last hit — gates when Field Repair is allowed to kick in. */
  private sinceDamageMs = 0;
  /** Sub-1-HP regen carry, so the integer hull only ever ticks up by whole HP. */
  private repairAccum = 0;

  constructor(maxHull: number, repairPerSec = 0) {
    this.maxHull = maxHull;
    this.hull = maxHull;
    this.repairPerSec = Math.max(0, repairPerSec);
    this.aim = { x: BALANCE.gun.muzzleX, y: BALANCE.VIEW.height * 0.42 };
  }

  /** Muzzle origin in screen px (bottom-centre, lifts with recoil). */
  muzzle(): Vec2 {
    return { x: BALANCE.gun.muzzleX, y: BALANCE.gun.muzzleY + this.recoil };
  }

  kick(): void {
    this.recoil = -6; // muzzle jumps up briefly
  }

  damage(amount: number): boolean {
    if (this.invulnMs > 0 || this.hull <= 0) return false;
    this.hull = Math.max(0, this.hull - amount);
    this.invulnMs = BALANCE.player.invulnMs;
    // Taking a hit restarts the out-of-contact clock — Field Repair only resumes
    // after the bird gets another quiet window.
    this.sinceDamageMs = 0;
    this.repairAccum = 0;
    return this.hull <= 0;
  }

  heal(n: number): void {
    if (this.hull > 0) this.hull = Math.min(this.maxHull, this.hull + n);
  }

  /** Pop a flare burst: opens the decoy window + starts the cooldown. */
  deployFlares(): boolean {
    if (this.flareCdMs > 0) return false;
    this.flareActiveMs = BALANCE.flares.activeMs;
    this.flareCdMs = BALANCE.flares.cooldownMs;
    return true;
  }

  update(dt: number): void {
    if (this.invulnMs > 0) this.invulnMs -= dt;
    if (this.recoil < 0) this.recoil = Math.min(0, this.recoil + (dt / 1000) * 80);
    if (this.flareActiveMs > 0) this.flareActiveMs = Math.max(0, this.flareActiveMs - dt);
    if (this.flareCdMs > 0) this.flareCdMs = Math.max(0, this.flareCdMs - dt);
    this.tickRepair(dt);
  }

  /** Field Repair: once the bird has been out of contact for the delay window,
   *  regenerate repairPerSec HP/sec (whole HP only, so the hull stays integral).
   *  A no-op when un-upgraded (repairPerSec 0), already full, or downed. */
  private tickRepair(dt: number): void {
    this.sinceDamageMs += dt;
    const ready =
      this.repairPerSec > 0 &&
      this.hull > 0 &&
      this.hull < this.maxHull &&
      this.sinceDamageMs >= BALANCE.player.autoRepairDelayMs;
    if (!ready) {
      this.repairAccum = 0;
      return;
    }
    this.repairAccum += (this.repairPerSec * dt) / 1000;
    if (this.repairAccum >= 1) {
      const whole = Math.floor(this.repairAccum);
      this.repairAccum -= whole;
      this.heal(whole);
    }
  }
}
